import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { Container, Spinner } from "reactstrap";
import dayjs from "dayjs";
import { getMonitoreoSalidas, getMonitoreoSalidasRuta } from "../../../helpers/backend_helper";
import { getStorageUser } from "../../../helpers/api_helper";
import { PROFILES } from "../../../components/VerticalLayout/SidebarContent";
import KpiTiles from "./KpiTiles";
import HourlyChart from "./HourlyChart";
import EstadoRutas from "./EstadoRutas";
import RutasTable from "./RutasTable";
import RutaDetail from "./RutaDetail";
import {
  CRIT_MIN,
  REFRESH_SEC,
  agoColor,
  agoTxt,
  estadoRuta,
  fechaLarga,
  hora,
  minutosDesde,
  normaliza,
} from "./estados";
import "./MonitoreoSalidas.css";

// Tipografias del diseño. Se inyectan como <link> y no con @import en el CSS:
// en el build de CRA un @import que no queda al inicio del bundle se ignora.
const FONTS_ID = "monitoreo-salidas-fonts";
const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@500;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";

const cargarFuentes = () => {
  if (document.getElementById(FONTS_ID)) return;
  const link = document.createElement("link");
  link.id = FONTS_ID;
  link.rel = "stylesheet";
  link.href = FONTS_URL;
  document.head.appendChild(link);
};

// Orden de "Más demoradas": criticas, demoradas, sin iniciar y al dia; dentro, por minutos
const SEVERIDAD = { crit: 3, late: 2, nodata: 1, ok: 0 };

/**
 * Monitoreo de salidas del dia por ruta. Solo Reseller (rutas de su empresa) y
 * SuperAdmin (todas). /monitoreo-salidas esta en la whitelist de App.js porque
 * ninguna policy cubre a ambos perfiles; el backend vuelve a validarlo.
 */
const MonitoreoSalidas = () => {
  document.title = "Monitoreo de salidas | Sistema de logística";

  // El guard va antes de cualquier hook para no romper el orden de hooks.
  if (![PROFILES.Reseller, PROFILES.SuperAdmin].includes(getStorageUser().profile_id)) {
    return <Redirect to="/dashboard" />;
  }

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stale, setStale] = useState(false);
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("delay");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMonitoreoSalidas();
      if (response?.status === true) {
        setData(response);
        setStale(false);
      } else {
        setStale(true);
      }
    } catch (error) {
      console.error(error);
      setStale(true);
    }
    setLoading(false);
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    cargarFuentes();
    cargar();
    const interval = setInterval(cargar, 1000 * REFRESH_SEC);
    return () => clearInterval(interval);
  }, [cargar]);

  const now = data?.now ?? dayjs().format("YYYY-MM-DD HH:mm:ss");

  const all = useMemo(
    () =>
      (data?.routes ?? []).map((r) => {
        const st = estadoRuta(r, now);
        // Sin salidas en curso la frescura no aplica; se muestra el ultimo dato del dia
        const ref = r.en_curso > 0 ? r.last_update : r.last_real;
        const min = minutosDesde(ref, now);
        return {
          id: r.id,
          name: r.name,
          cliente: r.cliente || "—",
          salidas: r.salidas,
          curso: r.en_curso,
          fin: r.finalizadas,
          pend: r.pendientes,
          st,
          min: r.en_curso > 0 ? min : null,
          lastTime: hora(ref, now),
          ago: agoTxt(min),
          agoColor: agoColor(st),
          barW: min === null ? 100 : Math.max(4, Math.min(100, Math.round((min / Math.max(CRIT_MIN * 2, 60)) * 100))),
        };
      }),
    [data, now]
  );

  const cnt = useMemo(() => {
    const c = { ok: 0, late: 0, crit: 0, nodata: 0 };
    all.forEach((r) => c[r.st]++);
    return c;
  }, [all]);

  const totals = useMemo(
    () => ({
      salidas: all.reduce((a, r) => a + r.salidas, 0),
      curso: all.reduce((a, r) => a + r.curso, 0),
      fin: all.reduce((a, r) => a + r.fin, 0),
      rutas: all.length,
      clientes: new Set(all.map((r) => r.cliente)).size,
    }),
    [all]
  );

  const worst = useMemo(
    () => all.filter((r) => r.min !== null).sort((a, b) => b.min - a.min)[0] ?? null,
    [all]
  );

  const rows = useMemo(() => {
    const q = normaliza(search.trim());
    const filtered = all.filter(
      (r) =>
        (filter === "all" || r.st === filter) &&
        (!q || normaliza(r.name).includes(q) || normaliza(r.cliente).includes(q))
    );
    return filtered.sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name, "es", { numeric: true });
      if (sort === "salidas") return b.salidas - a.salidas;
      return SEVERIDAD[b.st] - SEVERIDAD[a.st] || (b.min ?? -1) - (a.min ?? -1);
    });
  }, [all, filter, sort, search]);

  // Por defecto queda seleccionada la ruta con mayor demora
  useEffect(() => {
    if (selectedId === null && all.length) {
      setSelectedId((worst ?? all[0]).id);
    }
  }, [all, worst, selectedId]);

  const seleccionar = (id) => {
    if (id === selectedId) return;
    setDetail(null);
    setSelectedId(id);
  };

  // El detalle se refresca en el mismo tick que el resumen
  useEffect(() => {
    if (!selectedId) return;
    let vigente = true;
    setDetailLoading(true);
    getMonitoreoSalidasRuta(selectedId)
      .then((response) => {
        if (vigente && response?.status === true) setDetail(response);
      })
      .catch((error) => console.error(error))
      .finally(() => vigente && setDetailLoading(false));
    return () => {
      vigente = false;
    };
  }, [selectedId, tick]);

  const selected = all.find((r) => r.id === selectedId) ?? null;

  return (
    <div className="page-content">
      <Container fluid>
        <div className="monitoreo-salidas">
          <div className="ms-title-row">
            <div>
              <div className="ms-crumbs">
                <span>Soporte</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M9 6l6 6-6 6"></path>
                </svg>
                <strong>Monitoreo de salidas</strong>
              </div>
              <h1 style={{ marginTop: 8 }}>Monitoreo de salidas</h1>
              <p>
                Salidas del día por ruta y frescura de la última actualización · {fechaLarga(now)}, corte{" "}
                {dayjs(now).format("HH:mm")}
              </p>
            </div>
            <div className="ms-toolbar">
              <div className="ms-live">
                <span className="ms-dot" style={{ background: stale ? "#e5484d" : "#1aa251" }}></span>
                <span>
                  {stale
                    ? "Sin conexión · se reintenta en el siguiente ciclo"
                    : `En vivo · se actualiza cada ${REFRESH_SEC} s`}
                </span>
              </div>
              <button type="button" className="ms-btn" onClick={cargar} disabled={loading}>
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 12a9 9 0 1 1-3-6.7L21 8"></path>
                    <path d="M21 3v5h-5"></path>
                  </svg>
                )}
                Actualizar
              </button>
            </div>
          </div>

          {!data ? (
            <div className="ms-card ms-empty">
              {stale ? "No se pudo cargar el monitoreo de salidas." : <Spinner />}
            </div>
          ) : (
            <>
              <KpiTiles totals={totals} cnt={cnt} />

              <div className="ms-charts">
                <HourlyChart hours={data.hours ?? []} />
                <EstadoRutas cnt={cnt} total={all.length} worst={worst} />
              </div>

              <RutasTable
                rows={rows}
                total={all.length}
                cnt={cnt}
                filter={filter}
                setFilter={setFilter}
                sort={sort}
                setSort={setSort}
                search={search}
                setSearch={setSearch}
                selectedId={selectedId}
                onSelect={seleccionar}
              />

              <RutaDetail
                ruta={selected}
                salidas={detail?.route?.id === selectedId ? detail.salidas : null}
                now={detail?.now ?? now}
                loading={detailLoading}
              />
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default MonitoreoSalidas;
